import { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { ORDER_TABS, type OrderTab } from '@/features/orders/orderRecord';
import { OrderMissingPage, OrderRecordPage } from '@/features/orders/OrderRecordPage';

/*
  One order. `?edit=1` is the register's Edit button, the prototype's own parameter: the page
  opens with Edit order already up, then drops it from the address, so a reload or a shared link
  opens the order rather than the drawer again. The router reads "1" as a number, which is what
  the schema takes; anything else in it is ignored rather than throwing the route.

  `?tab=payments` opens Instalments & invoicing and `?tab=shipping` Shipping & returns, as the
  prototype's own `tab` parameter does, so the tab survives a reload and can be shared. Items is
  the page without one.
*/
const recordSearch = z.object({
    edit: z.literal(1).optional().catch(undefined),
    tab: z.enum(ORDER_TABS).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/orders/$orderId')({
    validateSearch: (search) => recordSearch.parse(search),
    component: OrderRoute,
});

function OrderRoute() {
    const { orderId } = Route.useParams();
    const { edit, tab } = Route.useSearch();
    const navigate = useNavigate();
    const onTabChange = useCallback(
        (next: OrderTab) =>
            void navigate({
                to: '/orders/$orderId',
                params: { orderId },
                search: next === 'items' ? {} : { tab: next },
                replace: true,
                resetScroll: false,
            }),
        [navigate, orderId],
    );
    const onEditHandled = useCallback(
        () =>
            void navigate({
                to: '/orders/$orderId',
                params: { orderId },
                search: tab ? { tab } : {},
                replace: true,
                resetScroll: false,
            }),
        [navigate, orderId, tab],
    );

    const id = Number(orderId);
    // A junk id in the URL is a dead link, read as an unknown order: the order's own
    // missing-record state, inside the app, rather than the API being asked about NaN or the bare
    // not-found page (ALS-296).
    if (!Number.isInteger(id) || id <= 0) {
        return <OrderMissingPage />;
    }
    return (
        <OrderRecordPage
            orderId={id}
            editRequested={edit === 1}
            onEditHandled={onEditHandled}
            tab={tab ?? 'items'}
            onTabChange={onTabChange}
        />
    );
}
